<template>
    <Modal>
        <TypeSelectScreen
            v-if="step === STEPS.TYPE_SELECT"
            :on-select="onActionSelect"
        />
        <CreateShowScreen
            v-if="step === STEPS.CREATE_SHOW"
            :on-submit="onShowSubmit"
        />
        <ImportShowScreen
            v-if="step === STEPS.IMPORT_SHOW"
            :on-submit="onShowSubmit"
        />
        <ImportShowConfirmScreen
            v-if="step === STEPS.CONFIRM_IMPORT"
            :on-continue="() => nextStep()"
        />
        <PlanSelectScreen
            v-if="step === STEPS.PLAN_SELECT"
            :on-select="onPlanSelect"
        />
        <TrialScreen
            v-if="step === STEPS.TRIAL"
            :on-continue="onTrialSelect"
        />
        <CheckoutScreen
            v-if="step === STEPS.CHECKOUT"
            :on-subscribe="onSubscribe"
        />
        <template v-slot:footer>
            <ModalFooter />
        </template>
    </Modal>
</template>
<script>

/**
 * External dependencies.
 */
import { mapState, mapActions, mapMutations } from 'vuex';

/**
 * Internal dependencies.
 */
import CheckoutScreen from './CheckoutScreen';
import CreateShowScreen from './CreateShowScreen';
import ImportShowConfirmScreen from './ImportShowConfirmScreen';
import ImportShowScreen from './ImportShowScreen';
import Modal from './Modal';
import ModalFooter from './ModalFooter';
import PlanSelectScreen from './PlanSelectScreen';
import TrialScreen from './TrialScreen';
import TypeSelectScreen from './TypeSelectScreen';
import { ACTION, STEPS } from './config';

export default {
    components: {
        CheckoutScreen,
        CreateShowScreen,
        ImportShowConfirmScreen,
        ImportShowScreen,
        Modal,
        ModalFooter,
        PlanSelectScreen,
        TrialScreen,
        TypeSelectScreen,
    },

    computed: {
        ...mapState('show/modal', [
            'step',
            'action',
            'show',
            'plan',
        ]),

        /**
         * Gets the show actions.
         *
         * @returns {object} Show actions.
         */
        ACTION() {
            return ACTION;
        },

        /**
         * Gets the steps.
         *
         * @returns {object} Steps.
         */
        STEPS() {
            return STEPS;
        },
    },

    /**
     * Handles the component creation.
     */
    async created() {
        await this.preloadData();
    },

    methods: {
        ...mapActions([
            'convertShow',
            'getBillingPlans',
            'loadPlanFeatures',
            'loadShowCategories',
            'loadUserSubscription',
            'updateUser',
        ]),

        ...mapActions('show/modal', [
            'nextStep',
            'previousStep',
            'closeModal',
        ]),

        ...mapMutations('register', {
            setRegisterFreshState: 'setFreshState',
        }),

        ...mapMutations('show/modal', [
            'setAction',
            'setShow',
            'setPlan',
            'setTrialEnabled',
            'setPromo',
            'setFreshState',
        ]),

        /**
         * Preloads data.
         *
         * @returns {Promise} Promises.
         */
        preloadData() {
            return Promise.all([
                this.loadUserSubscription(),
                this.getBillingPlans(),
                this.loadPlanFeatures(),
                this.loadShowCategories(),
            ]);
        },

        /**
         * Handles action selection.
         *
         * @param  {string} action Show action.
         */
        onActionSelect(action) {
            this.setShow(undefined);
            this.setAction(action);
            this.nextStep();
        },

        /**
         * Handles show submit.
         *
         * @param  {object} show Show object.
         */
        onShowSubmit(show) {
            this.setShow(show);
            this.nextStep();
        },

        /**
         * Handles plan select.
         *
         * @param  {string} planSlug Plan slug.
         */
        onPlanSelect(planSlug) {
            this.setPlan(planSlug);
            this.setPromo(null);
            this.nextStep();
        },

        /**
         * Handle trial select.
         *
         * @param  {boolean} trialEnabled Indicates whether trial was selected or not.
         */
        onTrialSelect({ trial }) {
            this.setTrialEnabled(trial);
            this.setPromo(! trial ? 'trial-trade-in' : null);
            this.nextStep();
        },

        /**
         * Handles subscribe action.
         *
         * @param  {object} subscription Subscription object.
         */
        async onSubscribe(subscription) {
            try {
                await this.convertShow({
                    subscriptionId: subscription.id,
                    showHash: this.show.hash,
                    planId: this.plan.id,
                });
            } catch (e) {
                //
                return;
            }

            if (this.$route.path !== `/shows/${this.show.hash}`) {
                this.$router.push(`/shows/${this.show.hash}`);
            }

            this.closeModal();
            this.setFreshState();
            this.setRegisterFreshState();

            await Promise.all([
                this.updateUser(),
                this.loadUserSubscription({ force: true }),
            ]);
        },
    },
};
</script>
<style scoped lang="scss">
    @use './ShowModal.scss';
</style>
